import type { CollectionTarget } from '../types/schedule.types';
import type { DateKey } from '../utils/dateKey';
import { wholeMonthsBetween } from '../utils/dateKey';

/**
 * Result of aligning `buildCollectionTargets` output to elemis's instalment
 * grid (#730).
 */
export interface AlignedCollectionTargets {
    /** Aligned targets: every period >= 1, at most one per period, ascending. */
    targets: CollectionTarget[];
    /** The final target's period. `undefined` only for an empty or underivable input. */
    numInstalments?: number;
}

/**
 * D5 branch (b), CHOSEN: the all-undated-basket fallback from
 * `buildCollectionTargets` — a single `byPeriod`-only entry with no `byDate`
 * — must NOT have a count derived for it. Deriving would turn "collect the
 * whole basket total at instalment 1" from an unconditional elemis rejection
 * into a valid, confirmable one-row schedule — the most aggressive collection
 * profile in the system — without the product sign-off the #598 gate in
 * `collectionTargets.ts` asks for. See the plan's D5 for the full reasoning.
 *
 * DELIBERATELY BROADER THAN `byPeriod === 1` (Copilot review, PR #732). The
 * only shape this can match in production *is* `{ byPeriod: 1, amount }`:
 * `buildCollectionTargets` emits a `byPeriod` target in exactly one place
 * (`collectionTargets.ts:127`) and it is always literally
 * `{ byPeriod: 1, amount: undatedMinor }`; `alignCollectionTargets` has
 * exactly one production caller (`PaymentStep.tsx:223`), which always passes
 * that function's output. A single `{ byPeriod: 9 }` cannot arise. The
 * predicate is nonetheless left matching ANY single `byPeriod`-only target,
 * because that is the fail-safe direction:
 *
 *   - Broad (as written): if `buildCollectionTargets` ever grows a second,
 *     differently-shaped single-`byPeriod` fallback, this declines, no count
 *     is sent, elemis's default of 12 rejects the request, and the new
 *     profile fails LOUDLY — forcing someone to review it deliberately.
 *   - Tight (`=== 1`): that same new profile would silently derive a count
 *     and render as a confirmable schedule — precisely the unsigned-off
 *     activation D5 exists to prevent.
 *
 * The plan specifies this predicate verbatim (D5, branch (b)). Tightening it
 * is a behaviour change needing its own decision, not a cleanup. Pinned by
 * the "single `{ byPeriod: 9 }` declines BY DESIGN" test in
 * `collectionPeriods.test.ts`.
 */
const isSingleByPeriodFallback = (targets: readonly CollectionTarget[]): boolean =>
    targets.length === 1 && targets[0].byPeriod !== undefined && targets[0].byDate === undefined;

/** Maps one target to its raw (unclamped) period, or `NaN` if underivable. */
const rawPeriodOf = (target: CollectionTarget, anchor: DateKey): number => {
    if (target.byDate !== undefined) {
        return wholeMonthsBetween(anchor, target.byDate);
    }
    if (target.byPeriod !== undefined) {
        return target.byPeriod;
    }
    return Number.NaN;
};

/**
 * Aligns `buildCollectionTargets` output to elemis's instalment grid and
 * derives the instalment count the request must carry (#730). Pure — no
 * transport, no React, no clock: the anchor is passed in so the caller owns
 * "when now is" (D1).
 *
 * Algorithm (see the plan's "Alignment algorithm" flowchart):
 * 1. Map every target to a period integer relative to `anchorDateKey`
 *    (`byDate` floors via `wholeMonthsBetween`; `byPeriod` is itself).
 * 2. Decline to derive on a non-finite period — fail loud, never guess
 *    (Power of Ten rule 5). Unreachable from the sole production caller:
 *    `buildCollectionTargets` only ever emits a `byDate` that already
 *    passed `toDateKey`'s `YYYY-MM-DD` regex. Kept because this function
 *    takes a public `readonly CollectionTarget[]`.
 * 3. Clamp each period to `>= 1` (B4) — a clamped target loses its date
 *    anchor and is re-emitted as `{ byPeriod: 1 }` (D4): a `by_date` before
 *    instalment 1 is exactly what probe I rejected.
 * 4. Collapse targets sharing a period, keeping the greater cumulative
 *    amount (ties, and any later target given targets arrive in ascending
 *    cumulative order, resolve to the later one) — D3, required because
 *    elemis renders `cumulative(period) == target` exactly (B5) and two
 *    different cumulative amounts on the same period cannot both hold.
 * 5. `numInstalments` = the largest period, so the final target lands
 *    exactly on the final instalment (B3).
 */
export const alignCollectionTargets = (
    targets: readonly CollectionTarget[],
    anchorDateKey: DateKey,
): AlignedCollectionTargets => {
    if (targets.length === 0) {
        return { targets: [], numInstalments: undefined };
    }

    if (isSingleByPeriodFallback(targets)) {
        return { targets: [...targets], numInstalments: undefined };
    }

    const rawPeriods = targets.map((target) => rawPeriodOf(target, anchorDateKey));
    if (rawPeriods.some((period) => !Number.isFinite(period))) {
        return { targets: [...targets], numInstalments: undefined };
    }

    const byPeriod = new Map<number, CollectionTarget>();
    targets.forEach((target, index) => {
        const rawPeriod = rawPeriods[index];
        const period = Math.max(rawPeriod, 1);
        const wasClamped = period !== rawPeriod;
        const aligned: CollectionTarget = wasClamped
            ? { byPeriod: 1, amount: target.amount }
            : { ...target };

        const existing = byPeriod.get(period);
        if (existing === undefined || aligned.amount >= existing.amount) {
            byPeriod.set(period, aligned);
        }
    });

    const sortedPeriods = Array.from(byPeriod.keys()).sort((a, b) => a - b);
    const alignedTargets = sortedPeriods.map((period) => byPeriod.get(period) as CollectionTarget);

    return {
        targets: alignedTargets,
        numInstalments: sortedPeriods[sortedPeriods.length - 1],
    };
};
